import * as tf from '@tensorflow/tfjs';
import { RGBtoHSV, analyzeSkinTone } from '../utils/colorUtils';
import { type SeasonUndertone } from '../utils/seasonUndertone';

export interface FaceDetectionResult {
  detected: boolean;
  confidence: number;
  boundingBox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  landmarks?: Array<{ x: number; y: number }>;
  faceQuality?: {
    brightness: number;
    contrast: number;
    sharpness: number;
    overall: number;
  };
  skinToneAnalysis?: {
    category: string;
    confidence: number;
    undertone: SeasonUndertone;
  };
}

export interface FaceQualityMetrics {
  brightness: number;
  contrast: number;
  sharpness: number;
  noise: number;
  overall: number;
}

class FaceDetectionService {
  private isInitialized: boolean = false;
  private faceDetectionModel: tf.GraphModel | null = null;
  private landmarkModel: tf.GraphModel | null = null;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize TensorFlow.js
      await tf.ready();

      // Try to load face detection models (if available)
      await this.loadModels();

      this.isInitialized = true;
    } catch (error) {
      console.error('FaceDetectionService: Failed to initialize:', error);
      // Mark as initialized to prevent app blocking since face detection is optional
      this.isInitialized = true;
    }
  }

  private async loadModels(): Promise<void> {
    try {
      // Use basic free detection
      // In production, load pretrained models
    } catch (error) {
      console.warn('FaceDetectionService: Models not available:', error);
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Enhanced face detection using multiple algorithms
   */
  async detectFace(imageData: ImageData): Promise<FaceDetectionResult> {
    try {
      if (!this.isInitialized) {
        throw new Error('FaceDetectionService not initialized');
      }

      // Multiple detection methods for better accuracy
      const results = await Promise.all([
        this.detectFaceBySkinColor(imageData),
        this.detectFaceByEdgeDetection(imageData),
        this.detectFaceByTemplateMatching(imageData),
      ]);

      // Combine results for final detection
      const combinedResult = this.combineDetectionResults(results);

      // Analyze face quality if face is detected
      if (combinedResult.detected) {
        const qualityMetrics = this.analyzeFaceQuality(
          imageData,
          combinedResult.boundingBox
        );
        combinedResult.faceQuality = {
          brightness: qualityMetrics.brightness,
          contrast: qualityMetrics.contrast,
          sharpness: qualityMetrics.sharpness,
          overall: qualityMetrics.overall,
        };
        combinedResult.skinToneAnalysis = await this.analyzeSkinToneInFace(
          imageData,
          combinedResult.boundingBox
        );
      }

      return combinedResult;
    } catch (error) {
      console.error('Error in face detection:', error);
      return { detected: false, confidence: 0 };
    }
  }

  /**
   * Face detection using skin color analysis
   */
  private async detectFaceBySkinColor(
    imageData: ImageData
  ): Promise<FaceDetectionResult> {
    const pixels = imageData.data;
    const width = imageData.width;
    const height = imageData.height;

    let skinPixelCount = 0;
    let totalPixels = 0;
    const skinPixels: Array<{ x: number; y: number }> = [];

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];

        // Skip transparent pixels
        if (pixels[i + 3] < 128) continue;

        totalPixels++;

        // Enhanced skin detection
        const hsv = RGBtoHSV(r, g, b);
        if (this.isSkinPixel(hsv)) {
          skinPixelCount++;
          skinPixels.push({ x, y });
        }
      }
    }

    const skinRatio = skinPixelCount / totalPixels;
    // Require at least 10 % of the frame to be skin-toned; lower values are
    // typically backgrounds, walls, or hands — not a face in the oval.
    const detected = skinRatio > 0.1;
    const confidence = Math.min(skinRatio * 3, 0.9);

    let boundingBox:
      | { x: number; y: number; width: number; height: number }
      | undefined;

    if (detected && skinPixels.length > 0) {
      boundingBox = this.calculateBoundingBoxFromSkinPixels(skinPixels);
    }

    return {
      detected,
      confidence,
      boundingBox,
    };
  }

  /**
   * Face detection using edge detection
   */
  private async detectFaceByEdgeDetection(
    imageData: ImageData
  ): Promise<FaceDetectionResult> {
    const width = imageData.width;
    const height = imageData.height;
    const pixels = imageData.data;

    // Simple edge detection using Sobel operator
    const edges = this.detectEdges(pixels, width, height);

    // Look for oval-like shapes (potential faces)
    const faceRegions = this.findOvalShapes(edges, width, height);

    if (faceRegions.length > 0) {
      const bestRegion = faceRegions[0]; // Take the largest region
      return {
        detected: true,
        confidence: 0.7,
        boundingBox: bestRegion,
      };
    }

    return { detected: false, confidence: 0 };
  }

  /**
   * Face detection using template matching
   */
  private async detectFaceByTemplateMatching(
    imageData: ImageData
  ): Promise<FaceDetectionResult> {
    // Simple template matching for face-like patterns
    const width = imageData.width;
    const height = imageData.height;
    const pixels = imageData.data;

    // Look for symmetric patterns (typical of faces)
    const symmetryScore = this.calculateSymmetryScore(pixels, width, height);

    // 0.78 threshold: real faces are highly symmetric; backgrounds, objects,
    // and hands typically score below this.
    if (symmetryScore > 0.78) {
      // Estimate face region based on symmetry
      const centerX = width / 2;
      const centerY = height / 2;
      const faceSize = Math.min(width, height) * 0.4;

      return {
        detected: true,
        confidence: symmetryScore,
        boundingBox: {
          x: centerX - faceSize / 2,
          y: centerY - faceSize / 2,
          width: faceSize,
          height: faceSize,
        },
      };
    }

    return { detected: false, confidence: 0 };
  }

  /**
   * Combine multiple detection results for better accuracy.
   *
   * Rules:
   *  - Skin-color detection (results[0]) MUST be positive; it is the most
   *    reliable signal for an actual face being present. Edge detection and
   *    template symmetry are both prone to false positives on their own.
   *  - At least one supplementary method (edge OR symmetry) must also agree,
   *    so that a warm-coloured wall alone does not pass.
   */
  private combineDetectionResults(
    results: FaceDetectionResult[]
  ): FaceDetectionResult {
    const [skinResult, edgeResult, symmetryResult] = results;

    // Skin color is the primary gate
    if (!skinResult?.detected) {
      return { detected: false, confidence: 0 };
    }

    // At least one supplementary method must confirm
    const supplementaryConfirmed =
      edgeResult?.detected || symmetryResult?.detected;

    if (!supplementaryConfirmed) {
      return { detected: false, confidence: 0 };
    }

    const validResults = results.filter((r) => r.detected);
    const avgConfidence =
      validResults.reduce((sum, r) => sum + r.confidence, 0) /
      validResults.length;

    const bestResult = validResults.reduce((best, current) =>
      current.confidence > best.confidence ? current : best
    );

    return {
      detected: true,
      confidence: Math.min(avgConfidence * 1.1, 0.95),
      boundingBox: bestResult.boundingBox,
    };
  }

  /**
   * Enhanced skin pixel detection
   */
  private isSkinPixel(hsv: { h: number; s: number; v: number }): boolean {
    // Enhanced skin detection criteria
    return (
      hsv.s >= 0.05 && // Minimum saturation
      hsv.s <= 0.9 && // Maximum saturation
      hsv.v >= 0.15 && // Minimum value
      hsv.v <= 0.95 && // Maximum value
      ((hsv.h >= 0 && hsv.h <= 60) || // Warm tones (yellow to red)
        (hsv.h >= 300 && hsv.h <= 360) || // Cool tones (pink to purple)
        (hsv.h >= 60 && hsv.h <= 120 && hsv.s < 0.4)) // Neutral tones (olive)
    );
  }

  /**
   * Calculate bounding box from skin pixel distribution
   */
  private calculateBoundingBoxFromSkinPixels(
    skinPixels: Array<{ x: number; y: number }>
  ): {
    x: number;
    y: number;
    width: number;
    height: number;
  } {
    if (skinPixels.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    let minX = skinPixels[0].x;
    let minY = skinPixels[0].y;
    let maxX = skinPixels[0].x;
    let maxY = skinPixels[0].y;

    for (const pixel of skinPixels) {
      minX = Math.min(minX, pixel.x);
      minY = Math.min(minY, pixel.y);
      maxX = Math.max(maxX, pixel.x);
      maxY = Math.max(maxY, pixel.y);
    }

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  /**
   * Simple edge detection using Sobel operator
   */
  private detectEdges(
    pixels: Uint8ClampedArray,
    width: number,
    height: number
  ): Uint8ClampedArray {
    const edges = new Uint8ClampedArray(width * height);

    // Sobel operators
    const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0,
          gy = 0;

        // Apply Sobel operators
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4;
            const gray = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;
            const kernelIdx = (ky + 1) * 3 + (kx + 1);
            gx += gray * sobelX[kernelIdx];
            gy += gray * sobelY[kernelIdx];
          }
        }

        const magnitude = Math.sqrt(gx * gx + gy * gy);
        edges[y * width + x] = magnitude > 50 ? 255 : 0;
      }
    }

    return edges;
  }

  /**
   * Find oval-like shapes in edge image
   */
  private findOvalShapes(
    edges: Uint8ClampedArray,
    width: number,
    height: number
  ): Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> {
    const regions: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
    }> = [];

    // Simple region growing to find connected components
    const visited = new Set<number>();

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        if (edges[idx] > 0 && !visited.has(idx)) {
          const region = this.growRegion(edges, width, height, x, y, visited);
          if (region.pixelCount > 100) {
            // Minimum region size
            regions.push(region.boundingBox);
          }
        }
      }
    }

    return regions;
  }

  /**
   * Region growing algorithm
   */
  private growRegion(
    edges: Uint8ClampedArray,
    width: number,
    height: number,
    startX: number,
    startY: number,
    visited: Set<number>
  ): {
    pixelCount: number;
    boundingBox: { x: number; y: number; width: number; height: number };
  } {
    const queue: Array<[number, number]> = [[startX, startY]];
    const regionPixels: Array<[number, number]> = [];

    let minX = startX,
      minY = startY,
      maxX = startX,
      maxY = startY;

    while (queue.length > 0) {
      const [x, y] = queue.shift()!;
      const idx = y * width + x;

      if (visited.has(idx)) continue;
      visited.add(idx);

      if (edges[idx] > 0) {
        regionPixels.push([x, y]);
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);

        // Add neighbors
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              const nIdx = ny * width + nx;
              if (!visited.has(nIdx)) {
                queue.push([nx, ny]);
              }
            }
          }
        }
      }
    }

    return {
      pixelCount: regionPixels.length,
      boundingBox: {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
      },
    };
  }

  /**
   * Calculate symmetry score for face detection
   */
  private calculateSymmetryScore(
    pixels: Uint8ClampedArray,
    width: number,
    height: number
  ): number {
    let symmetryScore = 0;
    let totalComparisons = 0;

    const centerX = Math.floor(width / 2);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < centerX; x++) {
        const leftIdx = (y * width + x) * 4;
        const rightIdx = (y * width + (width - 1 - x)) * 4;

        // Compare RGB values
        const leftGray =
          (pixels[leftIdx] + pixels[leftIdx + 1] + pixels[leftIdx + 2]) / 3;
        const rightGray =
          (pixels[rightIdx] + pixels[rightIdx + 1] + pixels[rightIdx + 2]) / 3;

        const difference = Math.abs(leftGray - rightGray);
        symmetryScore += 1 - difference / 255;
        totalComparisons++;
      }
    }

    return totalComparisons > 0 ? symmetryScore / totalComparisons : 0;
  }

  /**
   * Analyze face quality metrics
   */
  private analyzeFaceQuality(
    imageData: ImageData,
    _boundingBox?: { x: number; y: number; width: number; height: number }
  ): FaceQualityMetrics {
    const pixels = imageData.data;
    const width = imageData.width;
    const height = imageData.height;

    let brightness = 0;
    let contrast = 0;
    let sharpness = 0;
    let noise = 0;

    const grayValues: number[] = [];

    // Calculate brightness and collect gray values
    for (let i = 0; i < pixels.length; i += 4) {
      const gray = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
      grayValues.push(gray);
      brightness += gray;
    }

    brightness /= grayValues.length;

    // Calculate contrast
    const mean = brightness;
    let variance = 0;
    for (const gray of grayValues) {
      variance += Math.pow(gray - mean, 2);
    }
    variance /= grayValues.length;
    contrast = Math.sqrt(variance);

    // Calculate sharpness (edge strength)
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        const current = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;

        const left = (pixels[idx - 4] + pixels[idx - 3] + pixels[idx - 2]) / 3;
        const right = (pixels[idx + 4] + pixels[idx + 5] + pixels[idx + 6]) / 3;
        const top =
          (pixels[(y - 1) * width * 4 + x * 4] +
            pixels[(y - 1) * width * 4 + x * 4 + 1] +
            pixels[(y - 1) * width * 4 + x * 4 + 2]) /
          3;
        const bottom =
          (pixels[(y + 1) * width * 4 + x * 4] +
            pixels[(y + 1) * width * 4 + x * 4 + 1] +
            pixels[(y + 1) * width * 4 + x * 4 + 2]) /
          3;

        const edgeStrength =
          Math.abs(current - left) +
          Math.abs(current - right) +
          Math.abs(current - top) +
          Math.abs(current - bottom);
        sharpness += edgeStrength;
      }
    }

    sharpness /= width * height;

    // Calculate noise (high-frequency variations)
    for (let i = 1; i < grayValues.length - 1; i++) {
      const diff =
        Math.abs(grayValues[i] - grayValues[i - 1]) +
        Math.abs(grayValues[i] - grayValues[i + 1]);
      noise += diff;
    }
    noise /= grayValues.length;

    // Normalize metrics to 0-1 range
    const normalizedBrightness = Math.max(0, Math.min(1, brightness / 255));
    const normalizedContrast = Math.max(0, Math.min(1, contrast / 128));
    const normalizedSharpness = Math.max(0, Math.min(1, sharpness / 100));
    const normalizedNoise = Math.max(0, Math.min(1, 1 - noise / 50));

    const overall =
      (normalizedBrightness +
        normalizedContrast +
        normalizedSharpness +
        normalizedNoise) /
      4;

    return {
      brightness: normalizedBrightness,
      contrast: normalizedContrast,
      sharpness: normalizedSharpness,
      noise: normalizedNoise,
      overall,
    };
  }

  /**
   * Analyze skin tone within the detected face region
   */
  private async analyzeSkinToneInFace(
    imageData: ImageData,
    boundingBox?: { x: number; y: number; width: number; height: number }
  ): Promise<{
    category: string;
    confidence: number;
    undertone: SeasonUndertone;
  }> {
    if (!boundingBox) {
      return { category: 'Unknown', confidence: 0, undertone: 'autumn' };
    }

    const { x, y, width, height } = boundingBox;
    const pixels = imageData.data;
    const imageWidth = imageData.width;

    // Sample skin pixels from the face region
    const skinSamples: Array<{ r: number; g: number; b: number }> = [];

    for (let py = y; py < y + height; py += 5) {
      // Sample every 5th pixel
      for (let px = x; px < x + width; px += 5) {
        if (px >= 0 && px < imageWidth && py >= 0 && py < imageData.height) {
          const idx = (py * imageWidth + px) * 4;
          const r = pixels[idx];
          const g = pixels[idx + 1];
          const b = pixels[idx + 2];

          const hsv = RGBtoHSV(r, g, b);
          if (this.isSkinPixel(hsv)) {
            skinSamples.push({ r, g, b });
          }
        }
      }
    }

    if (skinSamples.length === 0) {
      return { category: 'Unknown', confidence: 0, undertone: 'autumn' };
    }

    // Calculate average skin tone
    const avgR =
      skinSamples.reduce((sum, sample) => sum + sample.r, 0) /
      skinSamples.length;
    const avgG =
      skinSamples.reduce((sum, sample) => sum + sample.g, 0) /
      skinSamples.length;
    const avgB =
      skinSamples.reduce((sum, sample) => sum + sample.b, 0) /
      skinSamples.length;

    const analysis = analyzeSkinTone(avgR, avgG, avgB);

    return {
      category: analysis.skinToneCategory,
      confidence: analysis.confidence,
      undertone: analysis.undertone,
    };
  }
}

export const faceDetectionService = new FaceDetectionService();
